import { reactive } from 'vue';

// Toast unico do app: um contrato, um motor, uma fila.
//
//   toast.success(t('SEGMENTS.CREATED'));
//   toast.error(t('ASSIGN.FAILED'), {
//     detail: t('ASSIGN.NOT_IN_INBOX', { name }),
//     action: { label: t('RETRY'), onClick: retry },
//   });
//   toast.success(t('CONVERSATION.RESOLVED'), { undo: () => reopen(id) });
//   toast.promise(upload(files), { loading, success, error });
//
// Regras (ver protótipo "Toasts app.smart"):
//   - o TIPO diz o que aconteceu: sucesso 3s, info 4s, atenção 6s,
//     erro fica até fechar, carregando fica até virar sucesso/erro;
//   - título curto + detalhe opcional + UMA ação (ou Desfazer);
//   - mensagem idêntica em 2s não repete: vira contador;
//   - erro de campo NÃO é toast (fica no campo).
//
// Fora de componente (helpers JS) não há `t()`: passe `{ translate: true }`
// e o Toaster traduz a chave.

export const TOAST_KINDS = ['success', 'error', 'warning', 'info', 'loading'];

export const TOAST_DURATION = {
  success: 3000,
  info: 4000,
  warning: 6000,
  error: 0, // fica até fechar
  loading: 0, // fica até o promise resolver
};

const DEDUPE_WINDOW_MS = 2000;

export const toastState = reactive({
  items: [],
});

let seq = 0;

const normalize = (kind, message, options = {}) => {
  const title = String(message ?? '').trim();
  return {
    kind,
    title,
    detail: options.detail ? String(options.detail) : '',
    action: options.action || null, // { label, onClick?, to? }
    undo: typeof options.undo === 'function' ? options.undo : null,
    duration: Number.isFinite(options.duration)
      ? options.duration
      : TOAST_DURATION[kind],
    translate: Boolean(options.translate),
  };
};

const remove = id => {
  const i = toastState.items.findIndex(t => t.id === id);
  if (i > -1) toastState.items.splice(i, 1);
};

const findDuplicate = entry => {
  const now = Date.now();
  return toastState.items.find(
    t =>
      t.kind === entry.kind &&
      t.title === entry.title &&
      !t.detail &&
      !entry.detail &&
      now - t.createdAt < DEDUPE_WINDOW_MS
  );
};

const show = (kind, message, options = {}) => {
  const entry = normalize(kind, message, options);
  if (!entry.title) return null;

  const dup = findDuplicate(entry);
  if (dup) {
    dup.count += 1;
    dup.createdAt = Date.now();
    dup.tick += 1; // reinicia o tempo na tela
    return dup.id;
  }

  const id = options.id || `toast-${(seq += 1)}`;
  const existing = toastState.items.find(t => t.id === id);
  if (existing) {
    // Mesmo id = atualiza no lugar (loading -> sucesso/erro)
    Object.assign(existing, entry, {
      createdAt: Date.now(),
      tick: existing.tick + 1,
    });
    return id;
  }

  toastState.items.push({
    id,
    ...entry,
    count: 1,
    createdAt: Date.now(),
    tick: 0,
  });
  return id;
};

const promise = (p, messages = {}, options = {}) => {
  const id = show('loading', messages.loading || '', options);
  return Promise.resolve(p).then(
    result => {
      const msg =
        typeof messages.success === 'function'
          ? messages.success(result)
          : messages.success;
      if (msg) show('success', msg, { ...options, id });
      else remove(id);
      return result;
    },
    error => {
      const msg =
        typeof messages.error === 'function'
          ? messages.error(error)
          : messages.error;
      if (msg) show('error', msg, { ...options, id });
      else remove(id);
      throw error;
    }
  );
};

export const toast = {
  success: (message, options) => show('success', message, options),
  error: (message, options) => show('error', message, options),
  warning: (message, options) => show('warning', message, options),
  info: (message, options) => show('info', message, options),
  loading: (message, options) => show('loading', message, options),
  show,
  promise,
  dismiss: remove,
  clear: () => toastState.items.splice(0),
};

export const useToast = () => toast;
